import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import DataListPage from "../../../components/DataListPage";
import DataListDateRange from "../../../components/DataListDateRange";
import TruncatedCell from "../../../components/TruncatedCell";
import company from "../../../services/companyUserService";
import { instanceToken } from "../../../utils/constant";
import { formatDetailDate } from "../../../utils/detailUtils";

export default function CompanyUsers() {
  const navigate = useNavigate();
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("companyUsers_page");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = sessionStorage.getItem("companyUsers_rowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ from: "", to: "" });

  const parse = useCallback((responseData) => {
    setUsers(responseData.company_user);
    setCount(responseData.count);
  }, []);

  const fetchAllData = useCallback(async (searchValue, filterValues) => {
    try {
      const res = await company.getAllUsersData(
        instanceToken.token,
        searchValue,
        filterValues
      );
      setData(res.data.company_user);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchUsers = useCallback(
    async (limit, offset, searchValue, filterValues) => {
      try {
        const res = await company.getAllUsers(
          instanceToken.token,
          limit,
          offset,
          searchValue,
          filterValues
        );
        parse(res.data);
      } catch (error) {
        console.error(error);
      }
    },
    [parse]
  );

  useEffect(() => {
    const offset = page * rowsPerPage;
    fetchAllData(search, filters);
    fetchUsers(rowsPerPage, offset, search, filters);
  }, [page, rowsPerPage, search, filters, fetchAllData, fetchUsers]);

  useEffect(() => {
    company.searchUser(instanceToken.token, search).catch(console.error);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem("companyUsers_page", newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("companyUsers_rowsPerPage", newRowsPerPage);
    setPage(0);
    sessionStorage.setItem("companyUsers_page", 0);
  };

  const exportRows = (rows) => {
    const dataToExport = rows.map((u) => ({
      System_ID: u.company_id,
      Company_ID: u.code,
      Company_name: u.company_name,
      Phone: u.phone_number,
      Email: u.email,
      Address: u.address,
      Role: u.role,
      Created_Date: u.createdAt || u.created_at,
      Otp_Exp_Time: u.otp_exp_time,
      One_Time_Password: u.one_time_password,
    }));

    const xls = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, xls, "Company Users");
    XLSX.writeFile(wb, `${new Date().toISOString()}.xlsx`);
  };

  const openDetail = (row) => navigate(`/companys/${row.userid}`);

  const columns = [
    { id: "userid", label: "ID", render: (row) => row.userid },
    {
      id: "company_id",
      label: "System ID",
      render: (row) => <TruncatedCell value={row.company_id} />,
    },
    {
      id: "code",
      label: "Company ID",
      render: (row) => <TruncatedCell value={row.code} />,
    },
    {
      id: "email",
      label: "Email",
      render: (row) => <TruncatedCell value={row.email} />,
    },
    {
      id: "company_name",
      label: "Company Name",
      render: (row) => <TruncatedCell value={row.company_name} />,
    },
    {
      id: "phone_number",
      label: "Phone Number",
      render: (row) => <TruncatedCell value={row.phone_number} />,
    },
    {
      id: "created_at",
      label: "Created Date",
      render: (row) =>
        formatDetailDate(row.createdAt || row.created_at) || "N/A",
    },
  ];

  return (
    <DataListPage
      title="Company User"
      rows={users || []}
      columns={columns}
      count={count}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      headerActions={[
        { label: "Export All Excel", onClick: () => exportRows(data) },
        { label: "Export to Excel", onClick: () => exportRows(users || []) },
      ]}
      search={{
        placeholder: "Search by name, email, phone, IDs...",
        onSearch: setSearch,
        live: true,
        showFilterButton: false,
      }}
      toolbarExtra={
        <DataListDateRange
          from={filters.from}
          to={filters.to}
          onChange={(next) => {
            setFilters(next);
            setPage(0);
          }}
          onClear={() => {
            setFilters({ from: "", to: "" });
            setSearch("");
            setPage(0);
          }}
        />
      }
      onView={openDetail}
      menuActions={[{ label: "View details", onClick: openDetail }]}
      getRowKey={(row) => row.userid}
      loading={!users}
    />
  );
}
